import React from 'react';
import PropTypes from 'prop-types';
import { useOrgEditForm } from '../../hooks/useAdminForms';
import { COPY, EDIT_TYPE_LABEL as TYPE_LABEL } from '../../utils/adminConsole';

// Edit an org at any level: rename, re-slug (login URL — warned), suspend/reactivate
// (cascades to the subtree, super-admin only) and re-parent (move). A dealer-admin
// only sees name + slug; status + move are super-admin only.
// Logic: hooks/useAdminForms (shared with the redesign).
function OrgEditModal({ org, orgs, isSuper, onClose, onSaved }) {
    const {
        name, setName, slug, setSlug, status, setStatus, parentId, setParentId, saving, error,
        moveOptions, canMove, canSuspend, slugChanged, save,
    } = useOrgEditForm({ org, orgs, isSuper, onSaved, onClose });

    return (
        <div className="ac-modal-backdrop" onClick={onClose}>
            <div className="ac-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ac-modal-head">
                    <h2>Edit {TYPE_LABEL[org.type].toLowerCase()}</h2>
                    <button className="ac-modal-x" onClick={onClose} aria-label="Close">×</button>
                </div>

                <label className="ac-field">
                    <span>Name</span>
                    <input value={name} autoFocus onChange={(e) => setName(e.target.value)} />
                </label>

                <label className="ac-field">
                    <span>Sign-in address</span>
                    <div className="ac-slug-row">
                        <span className="ac-slug-prefix">/login/</span>
                        <input value={slug} onChange={(e) => setSlug(e.target.value)} />
                    </div>
                </label>
                {slugChanged && (
                    <p className="ac-modal-hint" style={{ color: 'var(--pp-warn)' }}>
                        {COPY.slugWarning(TYPE_LABEL[org.type].toLowerCase())}
                    </p>
                )}

                {canSuspend && (
                    <div className="ac-field">
                        <span>Status</span>
                        <div className="ac-choices">
                            <button className={status === 'active' ? 'on' : ''} onClick={() => setStatus('active')}>Active</button>
                            <button className={status === 'suspended' ? 'on' : ''} onClick={() => setStatus('suspended')}>Suspended</button>
                        </div>
                        {status === 'suspended' && org.status !== 'suspended' && (
                            <p className="ac-modal-hint" style={{ color: 'var(--pp-warn)' }}>
                                {COPY.suspendWarning}
                            </p>
                        )}
                    </div>
                )}

                {canMove && (
                    <label className="ac-field">
                        <span>Move to</span>
                        <select value={parentId} onChange={(e) => setParentId(e.target.value)}>
                            <option value="__keep__">Keep where it is</option>
                            {moveOptions.map((o) => <option key={o.id || 'root'} value={o.id}>{o.label}</option>)}
                        </select>
                    </label>
                )}

                {error && <div className="ac-error">{error}</div>}

                <div className="ac-modal-actions">
                    <button className="ac-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="ac-btn-primary" onClick={save} disabled={saving || !name.trim()}>
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </div>
        </div>
    );
}

OrgEditModal.propTypes = {
    org: PropTypes.object.isRequired,
    orgs: PropTypes.array.isRequired,
    isSuper: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
};

export default OrgEditModal;
